import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle, MapPin, Calendar, ArrowRight, X, Mic, User, Mail, Building, Briefcase, FileText, Linkedin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: ""
  });
  const [speakerData, setSpeakerData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    topic: "",
    bio: "",
    linkedin: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSpeakerSubmitting, setIsSpeakerSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [speakerResult, setSpeakerResult] = useState(null);
  const [showPreRegister, setShowPreRegister] = useState(false);
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      setSubmitResult(data);
      
      if (data.success) {
        setFormData({ name: "", email: "", company: "" });
        setTimeout(() => setShowPreRegister(false), 2000);
      }
    } catch (error) {
      setSubmitResult({
        success: false,
        message: "Something went wrong. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSpeakerSubmit = async (e) => {
    e.preventDefault();
    setIsSpeakerSubmitting(true);
    setSpeakerResult(null);

    try {
      const response = await fetch(`${API}/speakers/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(speakerData)
      });
      
      const data = await response.json();
      setSpeakerResult(data);
      
      if (data.success) {
        setSpeakerData({ name: "", email: "", company: "", role: "", topic: "", bio: "", linkedin: "" });
        setTimeout(() => setShowSpeakerModal(false), 3000);
      }
    } catch (error) {
      setSpeakerResult({
        success: false,
        message: "Something went wrong. Please try again."
      });
    } finally {
      setIsSpeakerSubmitting(false);
    }
  };

  return (
    <div className="hero-container" data-testid="hero-container">
      {/* Cinematic Africa Background - Animated Image Slideshow */}
      <div className="africa-bg">
        <div className="africa-slideshow">
          <div className="africa-slide slide-1"></div>
          <div className="africa-slide slide-2"></div>
          <div className="africa-slide slide-3"></div>
          <div className="africa-slide slide-4"></div>
          <div className="africa-slide slide-5"></div>
        </div>
        <div className="africa-overlay"></div>
        <div className="gradient-overlay"></div>
        <div className="vignette-overlay"></div>
        <div className="noise-overlay"></div>
      </div>

      {/* Navigation */}
      <nav className="nav-bar" data-testid="nav-bar">
        <div className="nav-logo">
          <img src="/logo.svg" alt="StableAfrica" className="nav-logo-img" />
        </div>
        <button 
          className="pre-register-btn"
          onClick={() => setShowPreRegister(true)}
          data-testid="pre-register-btn"
        >
          Pre-Register
        </button>
      </nav>

      {/* Content */}
      <div className="hero-content">
        {/* Date & Location Badge */}
        <div className="event-badge" data-testid="event-badge">
          <div className="badge-item">
            <Calendar size={16} />
            <span>October 2nd, 2026</span>
          </div>
          <div className="badge-separator">|</div>
          <div className="badge-item">
            <MapPin size={16} />
            <span>Landmark Event Hall</span>
          </div>
        </div>

        {/* Main Headline */}
        <h1 className="main-headline" data-testid="main-headline">
          <span className="headline-line line-1">Shaping</span>
          <span className="headline-line line-2">Africa's</span>
          <span className="headline-line line-3">
            <span className="gradient-text">Financial</span>
          </span>
          <span className="headline-line line-4">Future</span>
        </h1>

        {/* Subheadline */}
        <p className="subheadline" data-testid="subheadline">
          The premier gathering connecting fintech leaders, policymakers, builders, 
          and investors driving Africa's digital economy forward.
        </p>

        {/* CTA Buttons */}
        <div className="cta-buttons" data-testid="cta-buttons">
          <button 
            className="cta-btn primary"
            onClick={() => setShowPreRegister(true)}
            data-testid="join-waitlist-btn"
          >
            <span>Join the Waitlist</span>
            <ArrowRight size={20} />
          </button>
          <button 
            className="cta-btn secondary"
            onClick={() => setShowSpeakerModal(true)}
            data-testid="apply-speaker-btn"
          >
            <Mic size={20} />
            <span>Apply to Speak</span>
          </button>
        </div>
      </div>

      {/* Social Links */}
      <div className="social-links" data-testid="social-links">
        <a href="https://x.com/stablesafrica" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="twitter-link">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        <a href="https://www.instagram.com/stableafrica/" target="_blank" rel="noopener noreferrer" className="social-link" data-testid="instagram-link">
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        </a>
      </div>

      {/* Pre-Register Modal */}
      {showPreRegister && (
        <div className="modal-overlay" data-testid="pre-register-modal" onClick={() => setShowPreRegister(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPreRegister(false)} data-testid="close-pre-register">
              <X size={24} />
            </button>
            <h2 className="modal-title">Pre-Register</h2>
            <p className="modal-subtitle">Be the first to secure your spot at StableAfrica Summit 2026</p>
            
            <form onSubmit={handleSubmit} className="modal-form" data-testid="waitlist-form">
              <div className="form-group">
                <div className="input-icon"><User size={18} /></div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                  data-testid="name-input"
                />
              </div>
              <div className="form-group">
                <div className="input-icon"><Mail size={18} /></div>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="form-input"
                  data-testid="email-input"
                />
              </div>
              <div className="form-group">
                <div className="input-icon"><Building size={18} /></div>
                <input
                  type="text"
                  placeholder="Company / Organization (Optional)"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="form-input"
                  data-testid="company-input"
                />
              </div>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="spinner" size={20} />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Pre-Register Now</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {submitResult && (
              <div className={`result-message ${submitResult.success ? 'success' : 'error'}`} data-testid="result-message">
                {submitResult.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span>{submitResult.message}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Speaker Application Modal */}
      {showSpeakerModal && (
        <div className="modal-overlay" data-testid="speaker-modal" onClick={() => setShowSpeakerModal(false)}>
          <div className="modal-content speaker-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSpeakerModal(false)} data-testid="close-speaker-modal">
              <X size={24} />
            </button>
            <h2 className="modal-title">Apply to Speak</h2>
            <p className="modal-subtitle">Share your expertise at StableAfrica Summit 2026</p>
            
            <form onSubmit={handleSpeakerSubmit} className="modal-form speaker-form" data-testid="speaker-form">
              <div className="form-row">
                <div className="form-group">
                  <div className="input-icon"><User size={18} /></div>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={speakerData.name}
                    onChange={(e) => setSpeakerData({ ...speakerData, name: e.target.value })}
                    required
                    className="form-input"
                    data-testid="speaker-name-input"
                  />
                </div>
                <div className="form-group">
                  <div className="input-icon"><Mail size={18} /></div>
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={speakerData.email}
                    onChange={(e) => setSpeakerData({ ...speakerData, email: e.target.value })}
                    required
                    className="form-input"
                    data-testid="speaker-email-input"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="input-icon"><Building size={18} /></div>
                  <input
                    type="text"
                    placeholder="Company / Organization"
                    value={speakerData.company}
                    onChange={(e) => setSpeakerData({ ...speakerData, company: e.target.value })}
                    className="form-input"
                    data-testid="speaker-company-input"
                  />
                </div>
                <div className="form-group">
                  <div className="input-icon"><Briefcase size={18} /></div>
                  <input
                    type="text"
                    placeholder="Your Role / Title"
                    value={speakerData.role}
                    onChange={(e) => setSpeakerData({ ...speakerData, role: e.target.value })}
                    className="form-input"
                    data-testid="speaker-role-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <div className="input-icon"><Mic size={18} /></div>
                <input
                  type="text"
                  placeholder="Proposed Talk Topic *"
                  value={speakerData.topic}
                  onChange={(e) => setSpeakerData({ ...speakerData, topic: e.target.value })}
                  required
                  className="form-input"
                  data-testid="speaker-topic-input"
                />
              </div>
              <div className="form-group">
                <div className="input-icon textarea-icon"><FileText size={18} /></div>
                <textarea
                  placeholder="Brief Bio & Speaking Experience *"
                  value={speakerData.bio}
                  onChange={(e) => setSpeakerData({ ...speakerData, bio: e.target.value })}
                  required
                  className="form-input form-textarea"
                  rows={4}
                  data-testid="speaker-bio-input"
                />
              </div>
              <div className="form-group">
                <div className="input-icon"><Linkedin size={18} /></div>
                <input
                  type="url"
                  placeholder="LinkedIn Profile URL (Optional)"
                  value={speakerData.linkedin}
                  onChange={(e) => setSpeakerData({ ...speakerData, linkedin: e.target.value })}
                  className="form-input"
                  data-testid="speaker-linkedin-input"
                />
              </div>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSpeakerSubmitting}
                data-testid="speaker-submit-btn"
              >
                {isSpeakerSubmitting ? (
                  <>
                    <Loader2 className="spinner" size={20} />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Application</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            {speakerResult && (
              <div className={`result-message ${speakerResult.success ? 'success' : 'error'}`} data-testid="speaker-result-message">
                {speakerResult.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span>{speakerResult.message}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HeroPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
